/**
 * Pure validation-report builders.
 *
 * Each normalizes a check's raw result into a uniform `ValidationReport` — the
 * shape every `validate` subcommand emits. The inputs are already-gathered data
 * (file contents for theme/fonts via `project.ts`, captured build output via
 * `build.ts`), so every builder here is pure and trivially unit-testable with no
 * filesystem and no subprocess.
 *
 * @module
 */
import { Effect } from "effect"
import {
  formatForAi,
  formatFontSystemForAi,
  validateFontSystem,
  validateThemeCss,
  type FontSystemInputs,
  type FontSystemValidationResult,
  type ThemeValidationResult,
} from "@repo/site-validations"

export type CheckName = "theme" | "fonts" | "build"

/**
 * The captured result of running the project build (produced by `runBuild` in
 * `build.ts`). Surfaced verbatim as a check's raw `detail` under `--json`.
 *
 * A non-zero `exitCode` is a build *failure*, not an error — `buildReport` turns
 * it into a failing `ValidationReport` carrying fix instructions.
 */
export interface BuildOutput {
  /** The command that was run, e.g. `pnpm run build`. */
  readonly command: string
  /** Process exit code; `0` means the build succeeded. */
  readonly exitCode: number
  /** Combined, interleaved stdout+stderr (tail-capped for large logs). */
  readonly output: string
}

/**
 * The normalized result of a single validation check.
 *
 * - `summary` — one-line human status.
 * - `aiText` — fix instructions for an agent harness; `null` when the check passed.
 * - `detail` — the raw check result, surfaced under `--json`.
 */
export interface ValidationReport {
  readonly check: CheckName
  readonly ok: boolean
  readonly summary: string
  readonly aiText: string | null
  readonly detail: ThemeValidationResult | FontSystemValidationResult | BuildOutput
}

/** Build the theme report from `theme.css` contents (or `null` if the file is absent). */
export const themeReport = (themeCss: string | null): Effect.Effect<ValidationReport> =>
  validateThemeCss(themeCss).pipe(
    Effect.map((result) => ({
      check: "theme" as const,
      ok: result.isValid,
      summary: result.summary,
      aiText: result.isValid ? null : formatForAi(result),
      detail: result,
    })),
  )

/** Build the font-system report from the four font-related files' contents. */
export const fontsReport = (inputs: FontSystemInputs): Effect.Effect<ValidationReport> =>
  validateFontSystem(inputs).pipe(
    Effect.map((result) => ({
      check: "fonts" as const,
      ok: result.isValid,
      summary: result.summary,
      aiText: result.isValid ? null : formatFontSystemForAi(result),
      detail: result,
    })),
  )

/** Fix instructions for an agent harness when the build failed. */
export const formatBuildForAi = (output: BuildOutput): string => {
  const log = output.output.trim()
  return [
    `The project build failed: \`${output.command}\` exited with code ${output.exitCode}.`,
    "Read the build output below, fix the reported errors, then re-run the build.",
    "",
    log.length > 0 ? log : "(the build produced no output)",
  ].join("\n")
}

/**
 * Build the build report from captured build output. Pure: `build.ts` runs the
 * subprocess and hands the result here. Exit code `0` is a pass; anything else is
 * a validation failure whose `aiText` carries the build log + fix guidance.
 */
export const buildReport = (output: BuildOutput): ValidationReport => {
  const ok = output.exitCode === 0
  return {
    check: "build",
    ok,
    summary: ok ? "build succeeded" : `build failed — \`${output.command}\` exited ${output.exitCode}`,
    aiText: ok ? null : formatBuildForAi(output),
    detail: output,
  }
}
